import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { getStoredAccessToken } from '../../auth/utils/authToken';
import { organisationsAPI } from '../api/organisationsAPI';
import {
  JOIN_PATH,
  captureJoinCodeFromFragment,
  clearPendingJoinCode,
  readPendingJoinCode,
} from '../pendingJoinCode';
import Logo from '../../../components/Logo/Logo';
import '../../auth/components/MagicLinkCallback.css';

// Where everyone lands after joining: /chat renders onboarding inline until
// it is complete, and is the normal landing page afterwards.
const AFTER_JOIN_PATH = '/chat';

// Refusals that will not change on a retry.
const MESSAGES = {
  already_member:
    'Your account already belongs to an organisation, so this code cannot add you to another one.',
  unknown_code: 'This code is not recognised. Check it with whoever gave it to you.',
  organisation_inactive: 'This organisation is not accepting new members at the moment.',
  platform_admin: 'Platform admin accounts manage organisations without joining them.',
};
// Neutral wording throughout: a code may belong to a school or a tutor, and
// the type is not known until the join response.
const RETRY_MESSAGE = 'We could not add you just now. Please try again.';

const refusalMessage = (err) => {
  const errorCode = err?.data?.error?.code;
  return Object.prototype.hasOwnProperty.call(MESSAGES, errorCode) ? MESSAGES[errorCode] : null;
};

/**
 * /join — how students and staff join a school.
 *
 * Student links only: a school shares /join#<student code>. The code is in
 * the fragment, which is never sent to a server; public/index.html's
 * join-link-guard and captureJoinCodeFromFragment move it into
 * sessionStorage and strip it from the URL before anything else runs.
 * Signed out, the student is sent to sign up (or log in) with the usual
 * `state.from` redirect back to /join, and the SSO and magic-link callbacks
 * return here too while a code is pending (postSignInPath). Signed in, the
 * code is sent to POST /api/organisations/join once and then cleared.
 *
 * Staff codes are never put in a link: a staff code opens every student in
 * a school. Staff open /join, sign in, and type the code into the staff
 * form, which sends it only in the request body.
 *
 * No code is ever rendered, logged or reported.
 */
const JoinOrganisationPage = () => {
  // Before anything else, including the first render's effects.
  useState(captureJoinCodeFromFragment);

  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const signedIn = isAuthenticated && Boolean(getStoredAccessToken());

  const [studentMessage, setStudentMessage] = useState(null);
  const [studentCanRetry, setStudentCanRetry] = useState(false);
  const [hasPendingCode, setHasPendingCode] = useState(() => Boolean(readPendingJoinCode()));
  const inFlight = useRef(false);

  const [staffCode, setStaffCode] = useState('');
  const [staffMessage, setStaffMessage] = useState(null);
  const [staffSubmitting, setStaffSubmitting] = useState(false);

  // React Router keeps its own copy of the URL; drop the fragment there too.
  useEffect(() => {
    if (location.hash) navigate(JOIN_PATH, { replace: true });
  }, [location.hash, navigate]);

  const joinWithStudentLink = useCallback(async (code) => {
    inFlight.current = true;
    setStudentMessage(null);
    try {
      await organisationsAPI.join(code);
      clearPendingJoinCode();
      navigate(AFTER_JOIN_PATH, { replace: true });
    } catch (err) {
      const refusal = refusalMessage(err);
      if (refusal) {
        clearPendingJoinCode();
        setHasPendingCode(false);
      }
      setStudentMessage(refusal || RETRY_MESSAGE);
      setStudentCanRetry(!refusal);
    } finally {
      inFlight.current = false;
    }
  }, [navigate]);

  useEffect(() => {
    if (isLoading || !hasPendingCode || inFlight.current || studentMessage) return;
    const code = readPendingJoinCode();
    if (!code) {
      setHasPendingCode(false);
      return;
    }
    if (!signedIn) {
      navigate('/login?signup=true', { replace: true, state: { from: { pathname: JOIN_PATH } } });
      return;
    }
    joinWithStudentLink(code);
  }, [isLoading, hasPendingCode, studentMessage, signedIn, joinWithStudentLink, navigate]);

  const retryStudentLink = () => {
    const code = readPendingJoinCode();
    if (code) {
      joinWithStudentLink(code);
    } else {
      setHasPendingCode(false);
      setStudentMessage(null);
    }
  };

  const submitStaffCode = async (event) => {
    event.preventDefault();
    const code = staffCode.trim();
    if (!code || staffSubmitting) return;
    setStaffSubmitting(true);
    setStaffMessage(null);
    try {
      await organisationsAPI.join(code);
      setStaffCode('');
      navigate(AFTER_JOIN_PATH, { replace: true });
    } catch (err) {
      setStaffMessage(refusalMessage(err) || RETRY_MESSAGE);
    } finally {
      setStaffSubmitting(false);
    }
  };

  const signInForStaff = () =>
    navigate('/login', { state: { from: { pathname: JOIN_PATH } } });

  let body;
  if (isLoading || (hasPendingCode && !studentMessage)) {
    body = (
      <div className="loading-state">
        <div className="premium-spinner-container">
          <div className="outer-pulse-ring"></div>
          <div className="inner-rotating-ring"></div>
        </div>
        <p style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
          Adding you now...
        </p>
      </div>
    );
  } else if (studentMessage) {
    body = (
      <div className="error-state" role="alert">
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: '32px', fontSize: '14px', lineHeight: '1.5' }}>
          {studentMessage}
        </p>
        <button
          type="button"
          className="login-button"
          style={{ width: '100%' }}
          onClick={studentCanRetry ? retryStudentLink : () => navigate(AFTER_JOIN_PATH, { replace: true })}
        >
          {studentCanRetry ? 'Try again' : 'Continue'}
        </button>
      </div>
    );
  } else if (!signedIn) {
    body = (
      <div>
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: '32px', fontSize: '14px', lineHeight: '1.5' }}>
          Staff: sign in first, then enter your staff code here.
        </p>
        <button type="button" className="login-button" style={{ width: '100%' }} onClick={signInForStaff}>
          Sign in
        </button>
      </div>
    );
  } else {
    body = (
      // Masked for Clarity, which keeps recording if the user reached
      // /join by navigating inside the app from a page that loaded it.
      <form onSubmit={submitStaffCode} data-clarity-mask="true">
        <label htmlFor="staff-code" style={{ display: 'block', marginBottom: '12px', color: 'var(--color-text-primary)', fontWeight: 600 }}>
          Enter your staff code
        </label>
        <input
          id="staff-code"
          name="staff-code"
          type="password"
          autoComplete="off"
          spellCheck={false}
          value={staffCode}
          onChange={(e) => setStaffCode(e.target.value)}
          style={{ width: '100%', marginBottom: '16px' }}
        />
        {staffMessage && (
          <p role="alert" style={{ color: 'var(--color-text-secondary)', marginBottom: '16px', fontSize: '14px' }}>
            {staffMessage}
          </p>
        )}
        <button type="submit" className="login-button" style={{ width: '100%' }} disabled={staffSubmitting || !staffCode.trim()}>
          Join as staff
        </button>
      </form>
    );
  }

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="login-overlay"></div>
      </div>
      <div className="login-container">
        <div className="login-content fade-in-up" style={{ textAlign: 'center', padding: '50px 40px' }}>
          <div className="login-header" style={{ justifyContent: 'center', marginBottom: '30px' }}>
            <div className="login-logo">
              <Logo onClick={() => navigate('/')} />
            </div>
          </div>
          <div className="callback-card-content">{body}</div>
        </div>
      </div>
    </div>
  );
};

export default JoinOrganisationPage;
